import { motion } from 'framer-motion';
import { MapPin, Navigation, Car, Clock } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

export default function LocationPage() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-[#1B4D3E]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">Location & Directions</h1>
            <p className="text-[#D4AF37] text-lg">Find your way to Big Bear Estate</p>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Large Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="aspect-[4/3]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d45600.84!2d-78.0833!3d44.7500!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cd3f0000000000%3A0x0!2sJack%20Lake!5e0!3m2!1sen!2sca!4v1700000000000"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Big Bear Estate Map"
                  ></iframe>
                </div>
              </div>
            </motion.div>

            {/* Location Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-6 h-6 text-[#1B4D3E]" />
                  <h3 className="font-serif text-xl text-[#1C1C1C]">Address</h3>
                </div>
                <p className="text-[#66605B]">
                  Big Bear Estate<br />
                  Jack Lake<br />
                  Apsley, Ontario<br />
                  Canada
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Car className="w-6 h-6 text-[#1B4D3E]" />
                  <h3 className="font-serif text-xl text-[#1C1C1C]">Distance From</h3>
                </div>
                <ul className="space-y-3 text-[#66605B]">
                  <li className="flex justify-between">
                    <span>Toronto (Downtown)</span>
                    <span className="font-medium">~2 hours</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Peterborough</span>
                    <span className="font-medium">~45 min</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Ottawa</span>
                    <span className="font-medium">~3 hours</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Apsley (town)</span>
                    <span className="font-medium">~10 min</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-[#1B4D3E]" />
                  <h3 className="font-serif text-xl text-[#1C1C1C]">Check-in Times</h3>
                </div>
                <ul className="space-y-2 text-[#66605B]">
                  <li><strong>Check-in:</strong> Friday 4:30 PM</li>
                  <li><strong>Check-out:</strong> Friday 11:00 AM</li>
                </ul>
              </div>
            </motion.div>
          </div>

          {/* Directions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12"
          >
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Navigation className="w-6 h-6 text-[#1B4D3E]" />
                <h2 className="font-serif text-2xl text-[#1C1C1C]">Driving Directions</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-[#1B4D3E] mb-3">From Toronto</h3>
                  <ol className="space-y-2 text-[#66605B] list-decimal list-inside">
                    <li>Take Highway 401 East to Highway 115 North</li>
                    <li>Continue on Highway 115 to Peterborough</li>
                    <li>Take Highway 28 North towards Apsley</li>
                    <li>Continue through Apsley to Jack Lake</li>
                    <li>Detailed directions provided upon booking confirmation</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold text-[#1B4D3E] mb-3">From Ottawa</h3>
                  <ol className="space-y-2 text-[#66605B] list-decimal list-inside">
                    <li>Take Highway 417 West to Highway 7 West</li>
                    <li>Continue on Highway 7 to Highway 28 South</li>
                    <li>Follow Highway 28 South past Bancroft to Apsley</li>
                    <li>Continue to Jack Lake area</li>
                    <li>Detailed directions provided upon booking confirmation</li>
                  </ol>
                </div>
              </div>

              <div className="mt-6 p-4 bg-[#F0EBE5] rounded-lg">
                <p className="text-sm text-[#66605B]">
                  <strong>Note:</strong> Detailed directions with GPS coordinates and property access instructions 
                  will be provided in your booking confirmation package.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
